import { FormikErrors } from 'formik';
import isEqual from 'lodash/isEqual';
import { Moment } from 'moment';
import type {
    Contest,
    ContestPolicy,
    ContestPolicyContentTranslation,
    ContestPolicyContentType,
    ContestPolicyKind,
    ContestPolicySave,
} from '../../../stores/contest';
import {
    convertLocalToUtcString,
    convertUtcToLocalMoment,
} from '../../../utils/timezone';
import type {
    PreviewPolicyContent,
    PreviewPolicyDraft,
} from '../contest-preview';
import { Language, LANGUAGES } from '../general-tab/form';

/** Lowercase letters and digits, joined by hyphens: an address. */
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/**
 * What is wrong with the brackets of a sign-up checkbox, which must hold one
 * pair around the text that opens the policy. Null when they are right.
 */
export function bracketsError(label: string): string | null {
    const opening = label.split('[').length - 1;
    const closing = label.split(']').length - 1;
    if (opening === 0 && closing === 0) {
        return 'Missing brackets: put them around the text that opens the policy, as in I accept the [contest rules]';
    }
    if (
        opening !== 1 ||
        closing !== 1 ||
        label.indexOf('[') > label.indexOf(']')
    ) {
        return 'Use one pair of brackets, [ then ], around the text that opens the policy';
    }
    if (/\[\s*\]/.test(label)) {
        return 'Put the text that opens the policy between the brackets';
    }
    return null;
}

/** One language as edited. A language left empty says nothing. */
export interface PolicyLanguageForm {
    title: string;
    urlSegment: string;
    acceptanceLabel: string;
    contentType: ContestPolicyContentType;
    body: string;
    documentUrl: string | null;
    externalUrl: string;
}

export interface PolicyForm {
    /** Null until saved. */
    id: number | null;
    /** Null until chosen. */
    kind: ContestPolicyKind | null;
    provinceIds: number[];
    translations: Record<Language, PolicyLanguageForm>;
    /** When the next version takes effect, in the contest's time zone. Null once saved. */
    effectiveAt: Moment | null;
}

export interface PoliciesFormValues {
    policies: PolicyForm[];
}

export const EMPTY_POLICIES_VALUES: PoliciesFormValues = { policies: [] };

function toLanguageForm(
    policy: ContestPolicy | undefined,
    lang: Language
): PolicyLanguageForm {
    const translation = policy?.translations.find((each) => each.lang === lang);
    const content = policy?.content?.translations.find(
        (each) => each.lang === lang
    );
    return {
        title: translation?.title ?? '',
        urlSegment: translation?.urlSegment ?? '',
        acceptanceLabel: translation?.acceptanceLabel ?? '',
        contentType: content?.contentType ?? 'html',
        body: content?.body ?? '',
        documentUrl: content?.documentUrl ?? null,
        externalUrl: content?.externalUrl ?? '',
    };
}

function toPolicyForm(policy: ContestPolicy | undefined): PolicyForm {
    return {
        id: policy?.id ?? null,
        kind: policy?.kind ?? null,
        provinceIds: policy?.provinceIds ?? [],
        translations: {
            en: toLanguageForm(policy, 'en'),
            fr: toLanguageForm(policy, 'fr'),
        },
        effectiveAt: null,
    };
}

export function newPolicyForm(): PolicyForm {
    return toPolicyForm(undefined);
}

export function toPoliciesFormValues(contest: Contest): PoliciesFormValues {
    return { policies: contest.policies.map(toPolicyForm) };
}

/**
 * The form to start over from once a save succeeds: the contest as reloaded,
 * or null when the reload failed or the form changed while the save ran,
 * which keeps those changes to save next.
 */
export function policiesAfterSave(
    submitted: PoliciesFormValues,
    current: PoliciesFormValues,
    reloaded: Contest | null
): PoliciesFormValues | null {
    return reloaded && isEqual(submitted, current)
        ? toPoliciesFormValues(reloaded)
        : null;
}

/**
 * True when the HTML shows something: a character that takes room on the
 * page, or an image with an `https` source. An editor left empty still holds
 * `<p></p>`.
 */
export function hasVisibleContent(html: string): boolean {
    const text = html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
        .trim();
    return text.length > 0 || /<img\b[^>]*\bsrc=["']?https:/i.test(html);
}

/** What a language says once sent, or null when it says nothing. */
function contentOf(
    form: PolicyLanguageForm,
    lang: Language
): ContestPolicyContentTranslation | null {
    const content = {
        lang,
        body: null,
        documentUrl: null,
        externalUrl: null,
    };
    switch (form.contentType) {
        case 'html':
            return hasVisibleContent(form.body)
                ? { ...content, contentType: 'html', body: form.body }
                : null;
        case 'pdf':
            return form.documentUrl
                ? {
                      ...content,
                      contentType: 'pdf',
                      documentUrl: form.documentUrl,
                  }
                : null;
        case 'external_url':
            return form.externalUrl.trim()
                ? {
                      ...content,
                      contentType: 'external_url',
                      externalUrl: form.externalUrl.trim(),
                  }
                : null;
    }
}

function toPolicySave(form: PolicyForm, timeZone: string): ContestPolicySave {
    const contents = LANGUAGES.flatMap((lang) => {
        const content = contentOf(form.translations[lang], lang);
        return content ? [content] : [];
    });
    return {
        id: form.id,
        kind: form.kind as ContestPolicyKind,
        provinceIds: form.provinceIds,
        // A language is named once it has a title or an address.
        translations: LANGUAGES.flatMap((lang) => {
            const translation = form.translations[lang];
            if (!translation.title.trim() && !translation.urlSegment.trim()) {
                return [];
            }
            return [
                {
                    lang,
                    title: translation.title.trim(),
                    urlSegment: translation.urlSegment.trim(),
                    acceptanceLabel: translation.acceptanceLabel.trim() || null,
                },
            ];
        }),
        content: {
            effectiveAt: form.effectiveAt
                ? convertLocalToUtcString(form.effectiveAt, timeZone)
                : null,
            translations: contents,
        },
    };
}

/** Called once the form is valid. */
export function toPolicies(
    values: PoliciesFormValues,
    timeZone: string
): ContestPolicySave[] {
    return values.policies.map((form) => toPolicySave(form, timeZone));
}

function toPreviewContent(
    content: ContestPolicyContentTranslation | null
): PreviewPolicyContent | null {
    switch (content?.contentType) {
        case 'html':
            return { type: 'html', body: content.body ?? '' };
        case 'pdf':
            return content.documentUrl
                ? { type: 'pdf', documentUrl: content.documentUrl }
                : null;
        case 'external_url':
            return content.externalUrl
                ? { type: 'external_url', externalUrl: content.externalUrl }
                : null;
        default:
            return null;
    }
}

/** A policy's id in the preview: its own once saved, a negative one before. */
export function previewIdOf(form: PolicyForm, index: number): number {
    return form.id ?? -(index + 1);
}

/**
 * The policies as edited, for the preview to show before any save. A policy
 * not yet saved takes a negative id of its own. A policy with no province
 * applies to the contest's, as the General tab holds them.
 */
export function toPreviewPolicies(
    values: PoliciesFormValues,
    provinces: {
        namesById: ReadonlyMap<number, string>;
        contestProvinceIds: number[];
    },
    timeZone: string
): PreviewPolicyDraft[] {
    const names = (ids: number[]) =>
        ids.flatMap((id) => {
            const name = provinces.namesById.get(id);
            return name ? [name] : [];
        });
    return values.policies.map((form, index) => ({
        id: previewIdOf(form, index),
        provinces: names(
            form.provinceIds.length > 0
                ? form.provinceIds
                : provinces.contestProvinceIds
        ),
        effectiveAt: form.effectiveAt
            ? convertLocalToUtcString(form.effectiveAt, timeZone)
            : null,
        translations: Object.fromEntries(
            LANGUAGES.map((lang) => {
                const translation = form.translations[lang];
                return [
                    lang,
                    {
                        title: translation.title.trim(),
                        urlSegment: translation.urlSegment.trim(),
                        acceptanceLabel:
                            translation.acceptanceLabel.trim() || null,
                        content: toPreviewContent(contentOf(translation, lang)),
                    },
                ];
            })
        ),
    }));
}

/** A saved policy the save would give a new version. */
export interface PolicyVersionChange {
    id: number;
    title: string;
    nextVersion: number;
    /** The latest version, in force or still to come. */
    latestVersion: number;
    /** The version in force and who accepted it, null when none is. */
    inForce: { version: number; participantCount: number } | null;
}

function sameContent(
    saved: ContestPolicy,
    sent: ContestPolicySave['content']
): boolean {
    // The editor ends its HTML with a line break, which the API drops.
    const said = (content: ContestPolicyContentTranslation) =>
        JSON.stringify([
            content.lang,
            content.contentType,
            content.body?.trim() ?? null,
            content.documentUrl,
            content.externalUrl,
        ]);
    const stored = (saved.content?.translations ?? []).map(said).sort();
    const next = (sent?.translations ?? []).map(said).sort();
    return JSON.stringify(stored) === JSON.stringify(next);
}

/**
 * The saved policies whose content or date the save changes, each of which
 * gets a new version. A policy never written before is left out: nobody
 * accepted it.
 */
export function versionChanges(
    values: PoliciesFormValues,
    contest: Contest
): PolicyVersionChange[] {
    return values.policies.flatMap((form) => {
        const saved = contest.policies.find((each) => each.id === form.id);
        if (!saved?.content) {
            return [];
        }
        const sent = toPolicySave(form, contest.timeZone).content;
        if (!form.effectiveAt && sameContent(saved, sent)) {
            return [];
        }
        const inForce = saved.versions.find(
            (version) => version.status === 'in_force'
        );
        return [
            {
                id: saved.id,
                title:
                    form.translations.en.title.trim() ||
                    form.translations.fr.title.trim() ||
                    `#${saved.id}`,
                nextVersion: saved.content.version + 1,
                latestVersion: saved.content.version,
                inForce: inForce
                    ? {
                          version: inForce.version,
                          participantCount: inForce.participantCount,
                      }
                    : null,
            },
        ];
    });
}

/** `1 participant`, `2 participants`. */
export function countParticipants(count: number): string {
    return count === 1 ? '1 participant' : `${count} participants`;
}

/** One line of the confirmation a save asks for before it publishes a version. */
export function describeVersionChange(change: PolicyVersionChange): string {
    const next = `${change.title}: version ${change.nextVersion} replaces version ${change.latestVersion}.`;
    if (!change.inForce) {
        return next;
    }
    const { version, participantCount } = change.inForce;
    return `${next} Version ${version} is in force, accepted by ${countParticipants(
        participantCount
    )}, who ${participantCount === 1 ? 'keeps' : 'keep'} it.`;
}

/** The latest version's date, shown in the contest's time zone. */
export function localDate(utc: string, timeZone: string): string {
    return (
        convertUtcToLocalMoment(utc, timeZone)?.format('YYYY-MM-DD HH:mm') ?? ''
    );
}

type PolicyErrors = FormikErrors<PolicyForm>;
type LanguageErrors = FormikErrors<PolicyLanguageForm>;

/** What a language lacks when it has no content, named by its field. */
const MISSING_CONTENT: Record<
    ContestPolicyContentType,
    keyof Pick<PolicyLanguageForm, 'body' | 'documentUrl' | 'externalUrl'>
> = {
    html: 'body',
    pdf: 'documentUrl',
    external_url: 'externalUrl',
};

/**
 * A language with a title or an address says something, and one that says
 * something has both: the drawer lists a policy in a language only then.
 */
function validateLanguage(
    form: PolicyLanguageForm,
    lang: Language
): LanguageErrors {
    const errors: LanguageErrors = {};
    const title = form.title.trim();
    const urlSegment = form.urlSegment.trim();
    const acceptanceLabel = form.acceptanceLabel.trim();
    const says = contentOf(form, lang) !== null;
    const named = Boolean(title || urlSegment) || says;
    if (named && !title) {
        errors.title = urlSegment
            ? 'Required with an address'
            : 'Required with content';
    } else if (title.length > 200) {
        errors.title = 'At most 200 characters';
    }
    if (named && !urlSegment) {
        errors.urlSegment = title
            ? 'Required with a title'
            : 'Required with content';
    } else if (urlSegment && !SLUG.test(urlSegment)) {
        errors.urlSegment = 'Lowercase letters and digits, joined by hyphens';
    } else if (urlSegment.length > 100) {
        errors.urlSegment = 'At most 100 characters';
    }
    if (acceptanceLabel && !named) {
        errors.acceptanceLabel = 'Give the policy a title and an address first';
    } else if (acceptanceLabel && bracketsError(acceptanceLabel)) {
        errors.acceptanceLabel = bracketsError(acceptanceLabel) as string;
    } else if (acceptanceLabel.length > 500) {
        errors.acceptanceLabel = 'At most 500 characters';
    }
    const url = form.externalUrl.trim();
    if (
        form.contentType === 'external_url' &&
        url &&
        (!/^https:\/\/[^\s]+$/.test(url) || url.length > 1024)
    ) {
        errors.externalUrl = 'An https:// address';
    } else if (named && !says) {
        errors[MISSING_CONTENT[form.contentType]] =
            form.contentType === 'pdf'
                ? 'Upload a PDF, or empty the title and the address'
                : 'Required with a title and an address';
    }
    return errors;
}

/**
 * The API's rules, shown before a round trip: a policy has a type and says
 * something, an address names one policy, and a date is to come.
 */
export function validatePolicies(
    values: PoliciesFormValues,
    contest: Contest | null,
    timeZone: string,
    now: Date = new Date()
): FormikErrors<PoliciesFormValues> {
    const segments = new Set<string>();
    const errors = values.policies.map((form) => {
        const policyErrors: PolicyErrors = {};
        if (!form.kind) {
            policyErrors.kind = 'Choose what the policy is';
        }

        const translations: FormikErrors<PolicyForm['translations']> = {};
        for (const lang of LANGUAGES) {
            const languageErrors = validateLanguage(
                form.translations[lang],
                lang
            );
            const segment = form.translations[lang].urlSegment.trim();
            if (segment && !languageErrors.urlSegment) {
                if (segments.has(`${lang}:${segment}`)) {
                    languageErrors.urlSegment =
                        'Another policy has this address';
                }
                segments.add(`${lang}:${segment}`);
            }
            if (Object.keys(languageErrors).length > 0) {
                translations[lang] = languageErrors;
            }
        }
        if (Object.keys(translations).length > 0) {
            policyErrors.translations = translations;
        }

        const content = toPolicySave(form, timeZone).content;
        if (content.translations.length === 0) {
            policyErrors.translations = {
                ...policyErrors.translations,
                en: {
                    ...(policyErrors.translations as
                        | Record<Language, LanguageErrors>
                        | undefined)?.en,
                    contentType: 'Write the policy in one language at least',
                },
            };
        }
        const effectiveAt = convertLocalToUtcString(form.effectiveAt, timeZone);
        if (effectiveAt && new Date(effectiveAt) <= now) {
            policyErrors.effectiveAt = 'A date to come' as never;
        }
        return policyErrors;
    });
    return errors.some((each) => Object.keys(each).length > 0)
        ? { policies: errors }
        : {};
}
